import React from "react";
import { Box, Typography, Divider, Avatar, Chip } from "@mui/material";
import {
  LocationOn as LocationOnIcon,
  HomeRepairService as HomeRepairServiceIcon,
  CurrencyRupee as CurrencyRupeeIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import DOMPurify from 'dompurify';


const JobDescriptionSidebar = ({ selectedJob, logourl }) => {
  if (!selectedJob || selectedJob === null) {
    return (
        <Box className='sidefeed-box job-openings-box my-2'>
                  <Typography>Select a job to see details.</Typography>
        </Box>
    )
    
    
  }

  // console.log("selectedJobNN", selectedJob)

  const parseSkillsArray = (data) => {
    if (!data) return [];
  
    try {
      const parsedData = JSON.parse(data);
  
      // Ensure parsedData is an array
      return Array.isArray(parsedData) ? parsedData : [];
    } catch (error) {
    
      return Array.isArray(data) ? data.map((item) => item.trim()) : [];
    }
  };

  const formatSalary = (salary) => {
    if (!salary) return "Not Specified"; // If salary is null, undefined, or empty string, return "Not Specified"
  
    // Check if the salary is already in 'k' format (e.g., '50k', '150k')
    if (/^\d+(\.\d+)?k$/.test(salary)) {
      return salary; // If it's in 'k' format, return the salary as is
    }
  
    // Check if the salary is a range (e.g., "10000-20000")
    const salaryParts = salary.split('-');
  
    if (salaryParts.length === 2) {
      const minSalary = parseInt(salaryParts[0].replace(/,/g, ""));
      const maxSalary = parseInt(salaryParts[1].replace(/,/g, ""));
      
      // Check if both values are valid numbers
      if (isNaN(minSalary) || isNaN(maxSalary)) {
        return "Not Specified"; // If either value is NaN, return "Not Specified"
      }
  
      // Convert salary range to 'k' format
      return `${minSalary / 1000}k - ${maxSalary / 1000}k`; 
    } else {
      const salaryInK = parseInt(salary.replace(/,/g, ""));
      
      // If the salary is not a valid number, return "Not Specified"
      if (isNaN(salaryInK)) {
        return "Not Specified";
      }
      
      return `${salaryInK / 1000}k`; // If it's a single salary, convert to 'k' format
    }
  };

  const renderPostedDate = (date) => {
    const calculateDaysAgo = (date) => {
      const postedDate = new Date(date);
      const today = new Date();
      const differenceInTime = today - postedDate;
      return Math.floor(differenceInTime / (1000 * 60 * 60 * 24));
    };

    const daysAgo = calculateDaysAgo(date);

    if (daysAgo === 0) {
      return "Posted today";
    } else if (daysAgo === 1) {
      return "Posted 1 day ago";
    } else {
      return `${daysAgo} days ago`;
    }
  };

  const createResponsibilityListItems = (data) => {
    if (!data) return <Typography variant="body2">No data available.</Typography>;
  
    let parsedData;
    try {
      parsedData = typeof data === "string" ? JSON.parse(data) : data;
    } catch (error) {
      return <Typography variant="body2">Invalid data format.</Typography>;
    }
  
    if (!Array.isArray(parsedData) || parsedData.length === 0) {
      return <Typography variant="body2">No data available.</Typography>;
    }
  
    return parsedData.map((line, index) => (
      <Box key={index} display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ fontSize: '0.80rem' }}>•</Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            fontSize: '0.80rem',
            whiteSpace: 'pre-wrap'
          }}
        >
          {line}
        </Typography>
      </Box>
    ));
  };

  return (
    <Link to={`/job-description/${selectedJob?.id}`} style={{ textDecoration: 'none' }}>
      <Box className='sidefeed-box job-openings-box' sx={{ 
        maxHeight: "83vh", 
        overflowY: "scroll", 
        scrollbarWidth: "none",
        p: 3,
        backgroundColor: '#f8f9fa',
        borderRadius: 2,
        boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px;',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          // transform: 'translateY(-2px)',
          boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px;'
        }
      }}>  
        {/* Company Header Section */}
        <Box sx={{ 
            gridColumn: '1 / -1',
            textAlign: 'right',
            color: 'text.secondary',
            fontSize: '0.875rem',
            paddingBottom: '0.5rem',
          }}>
            {renderPostedDate(selectedJob?.date_posted)}
          </Box>
          <Box
  sx={{
    borderRadius: "5px",
    px: "15px",
    py:"5px",
    backgroundImage:
    "linear-gradient(90deg, rgba(2, 0, 36, 1) 0%, rgba(0, 0, 105, 1) 0%, rgba(0, 212, 255, 1) 100%)",
    color: "white",

  }}
>
        <Box display="flex" alignItems="center" gap={3} mb={3}  >
        
          <Avatar
            src={selectedJob?.company_logo || 'https://www.shutterstock.com/image-vector/image-icon-trendy-flat-style-600nw-643080895.jpg'}
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'primary.main',
              fontSize: 24,
              fontWeight: 'bold',
              
            }}
          >
            {selectedJob?.company_name[0]}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ 
              color: 'white',
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 0.5
            }}>
              {selectedJob?.role}
            </Typography>
            <Typography variant="subtitle1" sx={{ 
              color: 'cyan',
              fontSize: '1rem',
              fontWeight: 500
              
            }}>
              {selectedJob?.company_name}
            </Typography>
          </Box>
        </Box>

     

        {/* Key Details Grid */}
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={3} color={"white"}>
          <DetailItem 
            icon={<LocationOnIcon sx={{ color: 'white' }} />}
            text={selectedJob?.location}
          />
          <DetailItem 
            icon={<HomeRepairServiceIcon sx={{ color: 'white',  }} />}
            text={selectedJob?.experience}
          />
          <DetailItem 
            icon={<CurrencyRupeeIcon sx={{ color: 'white' }} />}
            text={formatSalary(selectedJob?.salary)}
          />
        </Box>
        </Box>
        <Divider sx={{ 
          mb: 3, 
          borderColor: 'divider',
          borderWidth: 1 
        }} />

        {/* Job Metadata Section */}
        <Box display="grid" gap={2} mb={4}>
          <MetaItem label="Department Type" value={selectedJob?.dept_type} />
          <MetaItem label="Employment Type" value={selectedJob?.emp_type} />
          <MetaItem label="Education Level" value={selectedJob?.edu_level} />
          <MetaItem label="Work Mode" value={selectedJob?.mode} />
        </Box>

        {/* Skills Section */}
        <Box mb={4}>
          <SectionTitle>Key Skills</SectionTitle>
          <Box display="flex" gap={1} flexWrap="wrap">
            {parseSkillsArray(selectedJob?.skills || "[]").map((skill, index) => (
              <Chip 
                key={index} 
                label={skill} 
                size="small"
            
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ 
          mb: 3, 
          borderColor: 'divider',
          borderWidth: 1 
        }} />

        {/* Content Sections */}
        {/* <ContentSection 
          title="Job Summary"
          content={selectedJob?.job_summary}
        />
        
        <ContentSection 
          title="Responsibilities"
          content={createResponsibilityListItems(selectedJob?.responsibility)}
        />
        
        <ContentSection 
          title="Qualifications"
          content={createResponsibilityListItems(selectedJob?.qualifications)}
        />

       <ContentSection 
          title="Perks"
          content={createResponsibilityListItems(selectedJob?.perks)}
        />

        <ContentSection 
          title="About Company"
          content={selectedJob?.about_company}
        /> */}

        <Box
          className="job-description-content"
          bgcolor="white"
          borderRadius={2}
          p={3}
          mb={3}
        >
          {/* Render sanitized HTML */}
          <Box 
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(selectedJob?.job_description_md || "No information available.")
            }}
            sx={{
              '& h2': { 
                fontSize: '1rem', 
                fontWeight: 600,
                mt: 2,
                mb: 1,
                // color: 'primary.main'
              },
              '& ul': {
                pl: 2,
                mb: 1.5
              },
              '& li': {
                mb: 0.5
              }
            }}
          />
        </Box>
      </Box>
    </Link>
  );
};

const DetailItem = ({ icon, text }) => (
  <Box display="flex" alignItems="center" gap={1.5}>
    {icon}
    <Typography variant="body2" sx={{ 
      color: 'white',
      fontSize: '12px',
      fontWeight: 500,
    }}>
      {text}
    </Typography>
  </Box>
);

const MetaItem = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between">
    <Typography variant="subtitle2" sx={{ 
      color: 'text.secondary',
      fontWeight: 600,
      fontSize: '0.80rem' // Added font size
    }}>
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ 
      color: 'text.primary',
      fontWeight: 500,
      fontSize: '0.80rem' // Added font size
    }}>
      {value}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography variant="h6" sx={{ 
    color: 'text.primary',
    fontSize: '1rem',
    fontWeight: 700,
    mb: 2
  }}>
    {children}
  </Typography>
);

const ContentSection = ({ title, content }) => (
  <Box mb={4}>
    <SectionTitle>{title}</SectionTitle>
    <Typography variant="body2" sx={{
      color: 'text.secondary',
      fontSize: '0.80rem',
      whiteSpace: 'pre-wrap'
    }}>
      {content}
    </Typography>
  </Box>
)

export default JobDescriptionSidebar;
